/* 루미나 화면 층 신작 — 루미나 components/common/CasinoProviderGrid.jsx(블랙 로직 + SPEC §9-4 로비 팝업 단계)의 로직을 그대로 옮기고,
   카드만 홈 전용 HomeProviderGrid(.pgrid > .gcard)로 그린다.
   WHY: CasinoProviderGrid 는 카드 그리기(ProviderGrid, 슬롯·카지노 페이지 카드 .sl-card)와 로비 로직이 한 파일에 있어, 홈 카드를 시안 .gcard 로
        바꾸려면 로직을 그대로 둔 채 렌더만 바꾼 사본이 필요하다(원본 파일은 슬롯·카지노 페이지 담당이 관리). 아래 로직은 원본과 같다:
        인증 체크 → 로비 팝업(캐릭터·이름·코드·게임 수·대표 테이블·"로비 입장"·닫기) → "로비 입장" 시 벤더 코드 정규화 → 로비 API(launchVendorLobby)
        → 새 창 실행 + 닫힘 감지(1초) → 닫히면 확정 종료(endGame(true)), 실패 사유는 백엔드 메시지를 alert 로, 탭 닫힘(beforeunload) 시 sendBeacon 통보.
   ※ 사본 유지 비용을 없애려면 CasinoProviderGrid 가 카드 그리드 부품을 prop 으로 받게 하면 된다(리드 판단 대상, 보고서 ④). */
import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import HomeProviderGrid from './HomeProviderGrid';
import gamesService from '../../services/games';
import { getProviderNames } from '../../utils/providerUtils';
import { resolveApiImageUrl } from '../../utils/logoUtils';
import useAuth from '../../hooks/useAuth';

/* [2026-09-28] 첫 화면 카지노 게임사 카드도 같은 로비 절차를 쓰도록 바깥 요청을 받는다.
   lobbyRequest: 로비 팝업을 열 게임사(히어로 카드가 넘김) / onLobbyRequestHandled: 요청을 받았음을 알려 부모가 비운다 /
   showGrid=false: 카드 그리드 없이 로비 팝업만(히어로 전용 인스턴스). 로비 로직(인증·팝업·닫힘 감지·종료 통보)은 한 줄도 바꾸지 않는다.
   WHY: 카지노는 로비 입장이 규칙이고, 그 절차가 이 부품 안에만 있어 사본을 만들지 않고 그대로 쓰기 위함. */
const HomeCasinoProviderGrid = ({ providers, vendorNames = {}, lobbyRequest = null, onLobbyRequestHandled, showGrid = true }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // 로비 팝업 창 참조 및 닫힘 감지 interval 참조
  const lobbyPopupRef = useRef(null);
  const lobbyCheckIntervalRef = useRef(null);

  // [루미나] 로비 팝업(확인 단계)에 띄울 게임사 + 대표 테이블 이름 목록
  const [lobbyProvider, setLobbyProvider] = useState(null);
  const [lobbyTables, setLobbyTables] = useState([]);

  // 로비 팝업 닫힘 감지 시 게임 종료 API 호출
  const handleLobbyPopupClose = useCallback(() => {
    // [2026-08-21] 게임 창이 실제로 닫힌 것을 확인한 신호 → 확정 종료로 알린다(즉시 반영).
    console.log('[HomeCasinoProviderGrid] 로비 팝업 닫힘 감지 - 게임 종료 API 호출');
    gamesService.endGame(true).catch(err => {
      console.error('[HomeCasinoProviderGrid] 게임 종료 처리 실패:', err);
    });
    lobbyPopupRef.current = null;
  }, []);

  // 로비 팝업 닫힘 감지 interval 시작
  const startLobbyPopupMonitor = useCallback((popupWindow) => {
    // 기존 interval이 있으면 정리 (중복 방지)
    if (lobbyCheckIntervalRef.current) {
      clearInterval(lobbyCheckIntervalRef.current);
    }
    lobbyPopupRef.current = popupWindow;

    // 1초마다 팝업 닫힘 체크
    lobbyCheckIntervalRef.current = setInterval(() => {
      if (popupWindow.closed) {
        clearInterval(lobbyCheckIntervalRef.current);
        lobbyCheckIntervalRef.current = null;
        handleLobbyPopupClose();
      }
    }, 1000);
  }, [handleLobbyPopupClose]);

  // 컴포넌트 언마운트 시 interval 정리 및 게임 종료 처리
  useEffect(() => {
    return () => {
      if (lobbyCheckIntervalRef.current) {
        clearInterval(lobbyCheckIntervalRef.current);
        lobbyCheckIntervalRef.current = null;
      }
      /* [2026-08-21] 게임 창이 살아 있는데 화면만 벗어나는 경우는 종료를 알리지 않는다.
         WHY(실측): 카지노 게임은 별도 창에서 게임사 쪽으로 돌아간다. 회원이 다른 메뉴로 옮기거나
           새로고침해도 그 창은 그대로 열려 있고 게임도 이어진다. 그런데 종전에는 이때도 종료를
           알려, 실제로 베팅 중인 회원의 접속상태가 '온라인'으로 바뀌었다.
           창이 닫히는 것은 위의 닫힘 감지가 잡으므로 여기서 알릴 필요가 없다. */
      if (lobbyPopupRef.current && !lobbyPopupRef.current.closed) {
        console.log('[HomeCasinoProviderGrid] 화면 이탈 - 게임 창은 열려 있어 종료를 알리지 않음');
      }
    };
  }, []);

  /* [2026-08-21] 브라우저 탭을 닫을 때 게임 창 상태를 함께 알린다.
     판정: 게임 창이 이미 닫혀 있으면 게임이 확실히 끝난 것이므로 확정 종료로 알린다.
       아직 열려 있으면 게임은 게임사 쪽에서 이어지므로 확정으로 보내지 않는다(베팅으로 판단).
     방식: 창이 닫히는 중에는 일반 요청이 취소될 수 있어 브라우저의 전송 보장 수단을 쓴다. */
  useEffect(() => {
    const handleBeforeUnload = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      const popup = lobbyPopupRef.current;
      if (!popup) return;                      // 게임을 켠 적이 없으면 알릴 것이 없다
      const alreadyClosed = popup.closed === true;
      navigator.sendBeacon(
        '/api/games/end-session-beacon',
        JSON.stringify({ token, confirmed: alreadyClosed })
      );
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  /* [루미나] 로비 팝업의 대표 테이블 칩 — 게임사의 카지노 게임 목록 앞 몇 개(슬롯 화면과 같은 목록 API) 이름.
     WHY: 시안의 테이블 이름은 예시 배열이라 쓸 수 없다. 실제 목록이 없거나 실패하면 칩을 그리지 않는다(팝업 열림은 기다리지 않는다). */
  useEffect(() => {
    if (!lobbyProvider) { setLobbyTables([]); return undefined; }
    let alive = true;
    // [2026-11-01] 띄어쓰기를 밑줄로 바꾸지 않는다 — 게임사 표준 코드에 띄어쓰기가 있는 곳(NOLIMIT CITY·PG SOFT)은 밑줄 표기로 물으면 서버가 게임 0개를
    //   돌려줘 게임 목록이 비었다(2026-11-01 ur3 실측: 노리밋시티 139·PG소프트 177 → 0). 서버는 대소문자를 무시하고 표준 코드와 그대로 맞춘다.
    const vendor = (lobbyProvider.provider || lobbyProvider.provider_name || '').toLowerCase();
    gamesService.getGameList({ vendor, game_type: 'casino', limit: 6 })
      .then((data) => {
        if (!alive) return;
        const games = (data && (data.games || data.data)) || [];
        setLobbyTables(games.map((g) => g.game_name_ko || g.game_name).filter(Boolean).slice(0, 6));
      })
      .catch(() => { if (alive) setLobbyTables([]); });
    return () => { alive = false; };
  }, [lobbyProvider]);

  /* [루미나] 카드 클릭 = 로비 팝업 열기(블랙의 인증 체크는 여기서 먼저 한다 — 게스트는 팝업 없이 로그인으로) */
  const handleProviderClick = (provider) => {
    if (!user) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }
    setLobbyProvider(provider);
  };

  const closeLobbyPopup = () => setLobbyProvider(null);

  /* [2026-10-31] 로비 팝업을 ESC 로 닫는다 — 카지노 페이지(CasinoProviderGrid.jsx)와 같은 규칙.
     WHY: ESC 처리가 없어, 닫기(×)가 가려지던 휴대폰·좁은 화면에서는 바탕 끝을 눌러야만 닫혔다(2026-10-31 ur3 실측).
          알림창이 떠 있으면 알림창이 ESC 를 먼저 가져간다(utils/dialogKeyboard.js). */
  useEffect(() => {
    if (!lobbyProvider) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setLobbyProvider(null); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lobbyProvider]);

  /* [2026-09-28] 바깥(히어로 카드) 요청 → 카드 클릭과 같은 처리(게스트는 로그인으로, 회원은 로비 팝업) */
  useEffect(() => {
    if (!lobbyRequest) return;
    handleProviderClick(lobbyRequest);
    if (onLobbyRequestHandled) onLobbyRequestHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyRequest]);

  /* [루미나] "로비 입장" = 블랙 카드 클릭 본문(launchLobby) 그대로 호출 */
  const enterLobby = () => {
    const provider = lobbyProvider;
    setLobbyProvider(null);
    if (provider) launchLobby(provider);
  };

  const launchLobby = async (provider) => {
    console.log('[HomeCasinoProviderGrid] handleProviderClick called:', provider);

    // 인증 체크
    if (!user) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    try {
      const providerCode = provider.provider || provider.provider_name || provider.name || '';
      console.log('[HomeCasinoProviderGrid] providerCode:', providerCode);

      // Vendor code normalization
      const normalizeVendorCode = (code) => {
        const normalized = code.toUpperCase().replace(/\s+/g, '');
        const vendorMap = {
          'ASIAGAMING': 'AG',
          'DREAMGAMING': 'DG',
          'AG_CASINO': 'AG',
          'DG_CASINO': 'DG'
        };
        return vendorMap[normalized] || code.toUpperCase();
      };

      const normalizedCode = normalizeVendorCode(providerCode);
      console.log('[HomeCasinoProviderGrid] Normalized vendor code:', normalizedCode);

      // 모든 카지노 벤더: 로비 API 호출 후 팝업으로 실행
      console.log(`[HomeCasinoProviderGrid] Launching ${normalizedCode} lobby (popup mode)`);

      try {
        const lobbyData = await gamesService.launchVendorLobby(normalizedCode);

        if (lobbyData.success && (lobbyData.lobbyUrl || lobbyData.gameUrl)) {
          const lobbyUrl = lobbyData.lobbyUrl || lobbyData.gameUrl;
          if (lobbyData.fallback) {
            console.log(`[HomeCasinoProviderGrid] Using game fallback for ${normalizedCode}`);
          }
          console.log(`[HomeCasinoProviderGrid] Opening ${normalizedCode} lobby in popup:`, lobbyUrl?.substring(0, 100));
          // 로비 팝업 창 참조를 저장하고 닫힘 감지 시작
          const popupWindow = window.open(lobbyUrl, '_blank', 'width=1280,height=800,resizable=yes,scrollbars=yes');
          if (popupWindow) {
            startLobbyPopupMonitor(popupWindow);
          }
          console.log(`[HomeCasinoProviderGrid] ${normalizedCode} 로비 실행 완료`);
          return;
        } else {
          throw new Error('로비 URL을 받지 못했습니다.');
        }
      } catch (lobbyError) {
        // WHY: 카지노의 모든 활성 벤더는 로비 직진입이 정답. 로비 호출 실패(잔액·매핑·외부 API 등 어떤 사유든) 시
        // 게임 목록 폴백을 띄우면 회원이 거부 사유를 알 수 없으므로 백엔드가 보낸 사유 메시지(error 필드)를 그대로 노출한다.
        // services/games.js launchVendorLobby 가 axios 에러를 `throw error.response?.data || error` 로 이미 변환해 던지므로
        // lobbyError 는 plain object({ success:false, error:'...' }) 또는 Error.
        console.error(`[HomeCasinoProviderGrid] ${normalizedCode} 로비 실행 실패:`, lobbyError);
        // 안 읽은 쪽지 차단은 services/api.js 가 쪽지 안내 창을 연다 — 같은 내용의 알림창을 겹쳐 띄우지 않는다(2026-11-01 ur3 실측: 창 2개 겹침)
        if (lobbyError?.code === 'UNREAD_NOTICE_GATE') return;
        const reason = lobbyError?.error || lobbyError?.message || '로비를 열 수 없습니다.';
        alert(reason);
      }
    } catch (error) {
      console.error('[HomeCasinoProviderGrid] 게임 목록 로드 실패:', error);
      alert('게임 목록을 불러오는데 실패했습니다.');
    }
  };

  /* [루미나] 로비 팝업 표시값 — 전부 게임사 카드와 같은 응답·이름 규칙 */
  const lobbyView = (() => {
    const p = lobbyProvider;
    if (!p) return null;
    const code = p.provider || p.code || p.provider_name || p.name || '';
    const englishName = p.name || p.provider_name || code;
    const koreanName = p.name_ko || (vendorNames && vendorNames[code.toLowerCase()]) || getProviderNames(code).korean;
    const base = code.replace(/_casino|_slot/gi, '').toLowerCase();
    const characterSrc = p.character_hidden === true
      ? null
      : (p.character_url ? resolveApiImageUrl(p.character_url) : `/api/providers/characters/casino-${base}.png`);
    const gameCount = p.game_count != null && p.game_count !== '' && !isNaN(Number(p.game_count))
      ? Number(p.game_count).toLocaleString('ko-KR')
      : null;
    return { englishName, koreanName, characterSrc, gameCount };
  })();

  return (
    <>
      {showGrid && (
        <HomeProviderGrid
          providers={providers}
          type="casino"
          onProviderClick={handleProviderClick}
          vendorNames={vendorNames}
        />
      )}

      {/* 로비 팝업 — 카드 클릭과 로비 실행 사이의 확인 단계(시안 cs-lobby, 공용 다크 모달 lg). 카지노 페이지와 같은 구조
          [2026-10-31] 페이지 맨 바깥(body)에 그린다 — 카지노 페이지와 같은 이유(본문 겹침 층 1 안에서 상단 메뉴·하단 내비 밑에 깔림). */}
      {lobbyProvider && lobbyView && createPortal(
        <div className="lk-modal-ov cs-lobby-ov" onClick={closeLobbyPopup}>
          <div className="lk-modal lg dark" role="dialog" aria-modal="true" aria-label={`${lobbyView.koreanName} 카지노 로비`} onClick={(e) => e.stopPropagation()}>
            <div className="cs-lobby">
              <button className="cs-lobby-x" type="button" aria-label="닫기" onClick={closeLobbyPopup}>
                <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
              <div className="cs-lobby-art">
                {lobbyView.characterSrc && (
                  <img src={lobbyView.characterSrc} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
              </div>
              <div>
                <span className="tag">카지노 로비</span>
                <h3>{lobbyView.koreanName}</h3>
                <span className="cs-code">{lobbyView.englishName}</span>
                {lobbyView.gameCount && (
                  <div className="cs-lobby-stats">
                    <div><span className="cs-l">게임 수</span><span className="cs-v">{lobbyView.gameCount}</span></div>
                  </div>
                )}
                {lobbyTables.length > 0 && (
                  <div className="cs-tables">
                    {lobbyTables.map((name, i) => <span key={`${name}-${i}`}>{name}</span>)}
                  </div>
                )}
                <div className="cs-lobby-btns">
                  <button className="btn-primary dark" type="button" onClick={enterLobby}>
                    <span>로비 입장</span>
                    <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></span>
                  </button>
                  <button className="lk-btn ghost" type="button" onClick={closeLobbyPopup}>닫기</button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default HomeCasinoProviderGrid;
